import React, { useCallback, useEffect } from 'react'
import { Button, Modal } from 'react-bootstrap';
import { UpdatePermissionForm, UpdatePermissionSchema } from '../schema/UpdatePermissionSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import { useGetPermissionByID } from '../hooks/useGetPermissionByID';
import { toast } from 'sonner';
import FormSpinner from '../../../shared/components/spinner/FormSpinner';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { useUpdatePermission } from '../hooks/useUpdatePermission';
import { z } from 'zod';

interface UpdatePermissionDialogProps {
    show: boolean;
    onClose: () => void;
    permissionId: number | undefined;
}

const validationPermissionId = z.object({
    permissionId: z.number().int().positive(),
});

const UpdatePermissionDialog: React.FC<UpdatePermissionDialogProps> = ({
    show,
    onClose,
    permissionId
}) => {
    const { data: permission, isFetching, isError, isSuccess, error } = useGetPermissionByID({
        id: permissionId
    });

    if (isError) {
        toast.error(error?.message || 'Failed to fetch permission', {
            id: 'fetch-permission',
        });
        onClose();
        return;
    }

    const { register, handleSubmit, formState: { errors }, reset, setError } = useForm<UpdatePermissionForm>({
        resolver: zodResolver(UpdatePermissionSchema),
    })

    useEffect(() => {
        if (permission) {
            reset({
                name: permission.data.display_name ?? '',
                group: permission.data.group ?? '',
            });
        }
    }, [permission, reset]);

    const handleClose = () => {
        reset();
        onClose();
    }

    const queryClient = useQueryClient();

    const { mutate, isPending, isError: isUpdateError, error: updateError } = useUpdatePermission();

    if (isUpdateError) {
        toast.error(updateError?.message || 'invalid permission id', {
            id: 'fetch-permission',
        });

        handleClose();

        return;
    }

    const onSubmit = useCallback((values: UpdatePermissionForm) => {
        toast.loading('Updating permission...', {
            id: 'update-permission',
        });

        const validationIdResult = validationPermissionId.safeParse({ permissionId });

        if (!validationIdResult.success) {
            toast.error('Invalid permission id', {
                id: 'update-permission',
            });
            return;
        }

        mutate({
            permissionId: validationIdResult.data.permissionId,
            formData: values,
        }, {
            onSuccess: async () => {
                handleClose();

                await queryClient.refetchQueries({
                    predicate: (query) => query.queryKey.some((key) => ['permissions'].includes(key as string)),
                });

                toast.success('Permission updated successfully', {
                    id: 'update-permission',
                });
            },
            onError: (mutationError) => {
                if ((mutationError as any)?.status === 422) {
                    const validationErrors = (mutationError as any)?.response?.data?.errors;
                    if (validationErrors) {
                        for (const [field, messages] of Object.entries(validationErrors)) {
                            setError(field as keyof UpdatePermissionForm, {
                                type: "manual",
                                message: (messages as string[])[0],
                            });
                        }
                        toast.error('Failed to update permission', { id: 'update-permission' });
                    }
                } else {
                    toast.error(mutationError?.message || 'Failed to update permission', { id: 'update-permission' });
                }
            }
        });
    }, [mutate, permissionId]);

    return (
        <Modal
            show={show}
            onHide={handleClose}
            backdrop="static"
            keyboard={false}
            centered
        >
            <Modal.Header closeButton>
                <Modal.Title>Update Permission</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {isFetching && <FormSpinner />}
                {isSuccess && (
                    <form onSubmit={handleSubmit(onSubmit)} className="d-flex flex-column">
                        <div className="mb-3">
                            <label htmlFor="name" className="form-label">Name</label>
                            <input
                                type="text"
                                className={`form-control ${errors.name && 'is-invalid'}`}
                                id="name"
                                placeholder='Enter permission name'
                                {...register('name')}
                            />
                            {errors.name && <div className="invalid-feedback">{errors.name.message}</div>}
                        </div>
                        <div className='mb-3'>
                            <label htmlFor='group' className='form-label'>Group</label>
                            <input
                                type='text'
                                className={`form-control ${errors.group && 'is-invalid'}`}
                                id='group'
                                placeholder='Enter permission group'
                                {...register('group')}
                            />
                            {errors.group && <div className='invalid-feedback'>{errors.group.message}</div>}
                        </div>
                    </form>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    Close
                </Button>
                <Button variant="primary" type='submit' onClick={handleSubmit(onSubmit)} disabled={isPending}>
                    {isPending ? 'Saving...' : 'Save'}
                </Button>
            </Modal.Footer>
        </Modal >
    )
}

export default UpdatePermissionDialog